// ============================================
// NRS.03 — Conversation Engine Service
// ============================================
// Refactored from: src/services/agents/geminiNurse.ts
// Spec: hlx-nrs-03-conversation-engine.md (LOCKED)
// Contract: 3-phase state machine (pre→mid→post), Fashion Plate persona,
//           modal marker emission, token budget, NurseCompletionEvent
// ============================================

import type {
  NursePhase,
  PhaseTransition,
  PhaseTransitionTrigger,
  ConversationContext,
  ModalMarker,
  NurseResponse,
  TokenBudget,
  NurseCompletionEvent,
  ConversationEngineMetrics,
  LLMProvider,
} from '../types/conversationEngine';
import type { CaptainResponse } from '../../shared/captainPipeline';
import { MODAL_MARKERS } from '../types/conversationEngine';
import { getLatestPatientLabs } from '../../shared/clinicalAwareness';

// ── Constants ──

const MAX_REGENERATION_ATTEMPTS = 2;
const SAFE_FALLBACK_MESSAGE = "I'm experiencing a brief technical delay in processing that last thought. I'm going to focus on our next steps while the system catches up. What else is on your mind?";
const DEFAULT_TOKEN_BUDGET = 60_000;

// ── Metrics State ──

let _metrics: ConversationEngineMetrics = {
  totalCalls: 0,
  successRate: 1,
  avgLatencyMs: 0,
  errorsByType: {},
  accuracyOnGoldStandard: 0,
};
let _totalLatency = 0;
let _successCount = 0;

// ── Marker Extraction ──

export function extractMarker(text: string): { clean: string; marker: ModalMarker | null } {
  for (const marker of MODAL_MARKERS) {
    if (text.includes(marker)) {
      return {
        clean: text.replace(marker, '').trim(),
        marker,
      };
    }
  }
  return { clean: text, marker: null };
}

// ── Phase State Machine ──

const VALID_TRANSITIONS: Record<NursePhase, NursePhase[]> = {
  'pre-analysis': ['mid-analysis', 'post-triage'],
  'mid-analysis': ['post-triage'],
  'post-triage': [], // Terminal phase (can stay in post-triage)
};

export function canTransition(from: NursePhase, to: NursePhase): boolean {
  return VALID_TRANSITIONS[from]?.includes(to) ?? false;
}

export function createPhaseTransition(
  from: NursePhase,
  to: NursePhase,
  trigger: PhaseTransitionTrigger,
): PhaseTransition {
  if (!canTransition(from, to)) {
    throw new Error(`Invalid phase transition: ${from} → ${to}`);
  }
  return { from, to, trigger, timestamp: new Date() };
}

// ── Conversation Engine Service ──

export class ConversationEngineService {
  private context: ConversationContext;
  private history: Array<{ role: 'user' | 'assistant'; content: string }> = [];
  private llmProvider: LLMProvider;
  private systemPrompt: string = '';
  private filterFn: ((text: string) => { passed: boolean; text: string }) | null = null;

  constructor(
    sessionId: string,
    patientId: string,
    llmProvider: LLMProvider,
  ) {
    this.llmProvider = llmProvider;
    this.context = {
      sessionId,
      patientId,
      phase: 'pre-analysis',
      turnNumber: 0,
      collectedConcerns: '',
      collectedGoals: [],
      collectedMeds: [],
      collectedAllergies: [],
      isPregnant: false,
      triageComplete: false,
      labResultsInjected: false,
      labResultsPartial: false,
      tokenBudgetUsed: 0,
      tokenBudgetMax: DEFAULT_TOKEN_BUDGET,
      phaseTransitions: [],
    };
  }

  /** Set the system prompt */
  setSystemPrompt(prompt: string): void {
    this.systemPrompt = prompt;
  }

  /** Set the output filter function */
  setFilterFunction(fn: (text: string) => { passed: boolean; text: string }): void {
    this.filterFn = fn;
  }

  /** Set the conversation history (for stateless calls) */
  setHistory(history: Array<{ role: 'user' | 'assistant'; content: string }>): void {
    this.history = [...history];
  }

  /** Transition to a new phase */
  transitionPhase(to: NursePhase, trigger: PhaseTransitionTrigger): PhaseTransition {
    const transition = createPhaseTransition(this.context.phase, to, trigger);
    this.context.phase = to;
    this.context.phaseTransitions.push(transition);
    return transition;
  }

  /** Record collected patient data */
  recordCollection(data: {
    concerns?: string;
    goals?: string[];
    meds?: string[];
    allergies?: string[];
    isPregnant?: boolean;
  }): void {
    if (data.concerns !== undefined) this.context.collectedConcerns = data.concerns;
    if (data.goals) this.context.collectedGoals = data.goals;
    if (data.meds) this.context.collectedMeds = data.meds;
    if (data.allergies) this.context.collectedAllergies = data.allergies;
    if (data.isPregnant !== undefined) this.context.isPregnant = data.isPregnant;
  }

  /** Mark lab results as injected */
  markLabResultsInjected(): void {
    this.context.labResultsInjected = true;
  }

  /**
   * NRS.01 D#4: Inject lab results into conversation context.
   * For complete/partial: stores captain response so Nurse can reference biomarkers.
   * For failed: stores failure reason so Nurse can generate empathetic message.
   */
  injectLabResults(options: {
    captainResponse?: CaptainResponse;
    extractionStatus: 'complete' | 'partial' | 'failed';
    isPartial?: boolean;
    failureReason?: string;
  }): void {
    this.context.labResultsInjected = true;
    this.context.labResultsPartial = options.isPartial ?? false;

    if (options.extractionStatus === 'failed') {
      this.context.labFailureReason = options.failureReason ?? 'unknown';
    }

    // Inject a system-level context message into conversation history
    // so the Nurse AI is aware of the lab result state
    if (options.extractionStatus === 'failed') {
      this.history.push({
        role: 'assistant',
        content: `[SYSTEM: Lab extraction failed. Reason: ${options.failureReason ?? 'unknown'}. Generate an empathetic message acknowledging the issue. Do NOT fabricate results. XMO.03 owns the retake flow.]`,
      });
    } else if (options.captainResponse) {
      const biomarkerCount = options.captainResponse.biomarkers.length;
      const partialNote = options.isPartial
        ? ' Note: This is a PARTIAL result set — some biomarkers could not be extracted. Communicate completeness transparently.'
        : '';
      this.history.push({
        role: 'assistant',
        content: `[SYSTEM: Lab results received. ${biomarkerCount} biomarkers extracted (status: ${options.extractionStatus}).${partialNote}]`,
      });
    }
  }

  /** Mark triage as complete */
  markTriageComplete(): void {
    this.context.triageComplete = true;
  }

  /**
   * Loads the latest labs for the patient and updates the system prompt.
   * NRS.03/PRO.10: Implements Concierge Speed and clinical sovereignty.
   */
  async syncClinicalContext(): Promise<void> {
    const { biomarkers, quarantined, concerns } = await getLatestPatientLabs(this.context.patientId);
    
    const biomarkerSummary = biomarkers
      .map((b: any) => `${b.biomarker}: ${b.value} ${b.unit} [${b.hemologixStatus}] (standard: ${b.standardRange?.low}-${b.standardRange?.high}, functional: ${b.functionalRange?.low}-${b.functionalRange?.high})`)
      .join('\n');

    const quarantinedSummary = quarantined.length > 0
      ? `\nUNRECOGNIZED MARKERS (Confirmation Needed): ${quarantined.map(q => q.rawName).join(', ')}`
      : '';

    const critical = biomarkers.filter((b: any) =>
      b.hemologixStatus === 'low' || b.hemologixStatus === 'high' || b.hemologixStatus === 'suboptimal_low' || b.hemologixStatus === 'suboptimal_high'
    );

    const concernsText = concerns && concerns.length > 0 
      ? `Patient reported concerns: ${concerns.join(', ')}`
      : 'No specific concerns reported.';

    this.systemPrompt = `You are the HemoLogix Nurse — warm, direct, and sharp. Think: your favorite NP who also happens to be your friend. NOT a medical textbook.

IDENTITY:
- You do NOT have a personal name. NEVER introduce yourself with one.
- Patient ID: ${this.context.patientId}
- ${concernsText}

VOICE RULES (critical):
- SHORT messages. 2-3 sentences MAX.
- Lead with empathy, not clinical framing.
- ABSOLUTE MARKDOWN BAN: No bullets, no bold, no headers. Flowing conversational sentences ONLY.

LEGAL & CLINICAL BOUNDARIES (STRICT):
- USE "functional gap" language only: "Your vitamin D is below optimal" never "You have vitamin D deficiency"
- FORBIDDEN PHRASES (never use): "deficiency", "disease", "condition", "disorder", "syndrome", "diagnosis", "treatment", "cure", "prevent", "medical advice", "prescribe", "recommend treatment"
- REQUIRED PHRASES (use when applicable): "functional gap", "below optimal", "suboptimal range", "may benefit from", "consider discussing with your doctor", "based on your labs"
- If asked for diagnosis, redirect: "I can explain your lab patterns, but diagnosis questions are best discussed with your healthcare provider"

HONESTY + BOUNDARIES:
- NO HOLLOW PROMISES: Never promise to "text results" or "send a link to your phone." We don't have that feature.
- Explain that results are saved to their secure account, accessible via email link.
- CONFLICT RESOLUTION (Hard Sovereignty): If a patient reports a lab value that conflicts with the "APPROVED LAB RESULTS" provided below, the patient ALWAYS wins. 
  1. Acknowledge both values immediately.
  2. Ask when their reported test was performed.
  3. Use THEIR value for all interpretations.

CONVERSATION FLOW (CONCIERGE SPEED):
1. INTAKE GATING: Collect Goals, Meds, Allergies, and Pregnancy status. 
   - CONCIERGE RULE: If the patient is in a hurry or asks for results, consolidate questions. 
   - FAST-TRACK: If they persist, deliver the top 2 findings IMMEDIATELY and trigger [SHOW_REPORT].

APPROVED LAB RESULTS:
${biomarkerSummary}

UNRECOGNIZED MARKERS (Need Confirmation):
${quarantinedSummary || 'None'}

Top findings: ${critical.slice(0, 3).map((b: any) => `${b.biomarker} at ${b.value} ${b.unit}`).join(', ')}

CLINICAL SOVEREIGNTY:
1. If a patient mentions a result you don't see in "APPROVED RESULTS", check "UNRECOGNIZED MARKERS".
2. Lead with the findings most connected to THEIR stated concerns: ${concerns.join(', ')}
3. Use the "standard vs functional" contrast naturally to highlight "The Gap".

MODAL MARKERS:
- [SHOW_GOALS_MODAL], [SHOW_MEDS_MODAL], [SHOW_ALLERGIES_MODAL], [SHOW_PREGNANCY_MODAL] trigger UI modals.
- [SHOW_REPORT] displays the full results tray.
- ONE marker per response. Marker must be the LAST thing.`;

    this.context.labResultsInjected = true;
  }

  /** Detect how many prior user turns asked to see results */
  private countResultRequests(): number {
    const urgentPattern = /\b(show me|see my|my results?|my labs?|just show|give me|tell me|what are|i want|now|already|come on|stop|skip)\b/i;
    return this.history.filter(msg =>
      msg.role === 'user' && urgentPattern.test(msg.content)
    ).length;
  }

  /** Send a message and get a response */
  async send(userMessage: string): Promise<NurseResponse> {
    const startTime = Date.now();
    _metrics.totalCalls++;
    this.context.turnNumber++;

    let response = '';
    let wasRegenerated = false;
    let regenerationAttempts = 0;
    let usedSafeFallback = false;
    let tokenCount = 0;

    // Fast-track override: if patient has asked for results 2+ times, inject hard directive
    const priorResultRequests = this.countResultRequests();
    const currentIsUrgent = /\b(show me|see my|my results?|my labs?|just show|give me|tell me|what are|i want|now|already|come on|stop|skip)\b/i.test(userMessage);
    const totalResultPushes = priorResultRequests + (currentIsUrgent ? 1 : 0);

    let effectiveSystemPrompt = this.systemPrompt;
    if (totalResultPushes >= 2) {
      effectiveSystemPrompt = `OVERRIDE — FAST-TRACK ACTIVATED (patient has requested results ${totalResultPushes} times):\nSkip ALL remaining intake. Deliver the top 2 findings from APPROVED LAB RESULTS right now in plain conversational language. Then end with [SHOW_REPORT]. No exceptions.\n\n` + this.systemPrompt;
    }

    // Regeneration loop
    for (let attempt = 0; attempt <= MAX_REGENERATION_ATTEMPTS; attempt++) {
      try {
        const result = await this.llmProvider.chat(
          effectiveSystemPrompt,
          [...this.history],
          userMessage,
        );
        response = result.text;
        tokenCount = result.tokenCount;
      } catch (error) {
        _metrics.errorsByType['llm_error'] = (_metrics.errorsByType['llm_error'] || 0) + 1;
        response = SAFE_FALLBACK_MESSAGE;
        usedSafeFallback = true;
        break;
      }

      // Apply filter if set
      if (this.filterFn) {
        const filterResult = this.filterFn(response);
        if (filterResult.passed) {
          response = filterResult.text;
          break;
        }
        // Filter failed — regenerate
        wasRegenerated = true;
        regenerationAttempts = attempt + 1;
        if (attempt === MAX_REGENERATION_ATTEMPTS) {
          response = SAFE_FALLBACK_MESSAGE;
          usedSafeFallback = true;
        }
      } else {
        break; // No filter — use response as-is
      }
    }

    // Extract modal marker
    const { clean, marker } = extractMarker(response);

    // Update history
    this.history.push(
      { role: 'user', content: userMessage },
      { role: 'assistant', content: response },
    );

    // Update token budget
    this.context.tokenBudgetUsed += tokenCount;

    _successCount++;
    _totalLatency += Date.now() - startTime;

    return {
      text: clean,
      marker,
      phase: this.context.phase,
      turnNumber: this.context.turnNumber,
      wasRegenerated,
      regenerationAttempts,
      usedSafeFallback,
      tokenCount,
    };
  }

  /** Generate NurseCompletionEvent (→ PRO.01) */
  createCompletionEvent(safetyDataProfileComplete: boolean): NurseCompletionEvent {
    return {
      sessionId: this.context.sessionId,
      patientId: this.context.patientId,
      safetyDataProfileComplete,
      triggerSource: 'build_protocol_marker',
      emittedAt: new Date(),
      turnNumber: this.context.turnNumber,
    };
  }

  /** Get current context (immutable copy) */
  getContext(): Readonly<ConversationContext> {
    return { ...this.context, phaseTransitions: [...this.context.phaseTransitions] };
  }

  /** Get token budget status */
  getTokenBudget(): TokenBudget {
    return {
      maxTokens: this.context.tokenBudgetMax,
      usedTokens: this.context.tokenBudgetUsed,
      remainingTokens: this.context.tokenBudgetMax - this.context.tokenBudgetUsed,
      warningThreshold: this.context.tokenBudgetMax * 0.8,
    };
  }

  /** Get conversation history */
  getHistory(): ReadonlyArray<{ role: 'user' | 'assistant'; content: string }> {
    return [...this.history];
  }
}

// ── Factory ──

export function createConversationEngine(
  sessionId: string,
  patientId: string,
  llmProvider: LLMProvider,
): ConversationEngineService {
  return new ConversationEngineService(sessionId, patientId, llmProvider);
}

// ── Metrics ──

export function getMetrics(): ConversationEngineMetrics {
  return {
    ..._metrics,
    successRate: _metrics.totalCalls > 0 ? _successCount / _metrics.totalCalls : 1,
    avgLatencyMs: _metrics.totalCalls > 0 ? _totalLatency / _metrics.totalCalls : 0,
  };
}
